import { isValidSelectedStyleTab, productionDepartments, selectedStyleTabUrlKey } from "./data";

import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Fab from "@mui/material/Fab";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { attrGroupLinkedTables } from "components/PimDashboard/data";
import { PimDashboardState } from "context/PimDashboardContext";

import StyleAttributes from "./Departments/StyleAttributes/StyleAttributes";
import FabricAndTrims from "./Departments/FabricAndTrims/FabricAndTrims";

const PendingAttrsBanner = ({ pendingFields = [] }) => {
	const { selectedStyle } = PimDashboardState();
	const [open, setOpen] = useState(false);
	const styleId = selectedStyle?.[attrGroupLinkedTables.style.key]?.style_id;

	useEffect(() => {
		setOpen(false);
	}, [styleId]);

	if (!pendingFields.length) return null;

	const label = `${pendingFields.length} Attributes Pending`;

	return (
		<Box
			sx={{
				mb: 2,
				borderRadius: 3,
				border: "1px solid rgba(159,18,57,0.28)",
				bgcolor: "#fff",
				boxShadow: "0 10px 28px rgba(15,23,42,0.05)",
				overflow: "hidden",
			}}>
			<Box
				component="button"
				type="button"
				onClick={() => setOpen((prev) => !prev)}
				sx={{
					width: "100%",
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					px: 2,
					py: 1.25,
					border: "none",
					bgcolor: "#fff5f5",
					cursor: "pointer",
				}}>
				<Typography sx={{ fontWeight: 700, color: "#9f1239", fontSize: 14 }}>{label}</Typography>
				{open ? <ExpandLess sx={{ color: "#9f1239" }} /> : <ExpandMore sx={{ color: "#9f1239" }} />}
			</Box>
			<Collapse in={open}>
				<Box sx={{ px: 2, py: 1.5, display: "flex", flexWrap: "wrap", gap: 1 }}>
					{pendingFields.map((attr) => (
						<Box
							key={attr.key}
							sx={{
								px: 1.25,
								py: 0.4,
								borderRadius: 999,
								bgcolor: "#9f1239",
								color: "#fff",
								fontSize: 12,
								fontWeight: 600,
							}}>
							{attr.name}
						</Box>
					))}
				</Box>
			</Collapse>
		</Box>
	);
};

const SelectedStyle = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const [pendingFields, setPendingFields] = useState([]);
	const activeDepartment = useMemo(() => {
		const tabFromUrl = searchParams.get(selectedStyleTabUrlKey);
		return isValidSelectedStyleTab(tabFromUrl) ? tabFromUrl : productionDepartments.STYLE_ATTRIBUTES.key;
	}, [searchParams]);

	const handleDepartmentChange = (e, val) => {
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			next.set(selectedStyleTabUrlKey, val);
			return next;
		});
	};

	const tabComponents = [
		{
			...productionDepartments.STYLE_ATTRIBUTES,
			component: <StyleAttributes foreignKey={""} onPendingFieldsChange={setPendingFields} />,
		},
		{
			...productionDepartments.FABRIC_TRIMS,
			component: <FabricAndTrims isActive={activeDepartment === productionDepartments.FABRIC_TRIMS.key} />,
		},
	];
	let scroll = 600;
	const handleScroll = () => {
		if (scroll > 2300) scroll = 0;
		window.scrollTo({ top: scroll, behavior: "smooth" });
		scroll += 600;
	};
	return (
		<Box
			sx={{
				width: "100%",
				minHeight: "100%",
				bgcolor: "#f6f7fb",
				py: { xs: 1.5, md: 2.5 },
			}}
			position="relative">
			<Box
				sx={{
					width: "100%",
					maxWidth: 1600,
					mx: "auto",
					px: { xs: 1.25, sm: 2, md: 3 },
					boxSizing: " border-box",
				}}>
				<PendingAttrsBanner pendingFields={pendingFields} />
				<Box
					sx={{
						p: { xs: 1.25, md: 1.5 },
						borderRadius: 3,
						border: "1px solid rgba(0,0,0,0.08)",
						bgcolor: "#fff",
						boxShadow: "0 10px 28px rgba(15,23,42,0.05)",
						mb: 2,
						boxSizing: " border-box",
					}}>
					<Tabs
						value={activeDepartment}
						onChange={handleDepartmentChange}
						variant="scrollable"
						scrollButtons="auto"
						sx={{
							"& .MuiTab-root": {
								textTransform: "none",
								fontWeight: 700,
								minHeight: 44,
								fontSize: 13,
							},
							"& .MuiTabs-indicator": {
								height: 3,
								borderRadius: 999,
							},
						}}>
						{Object.keys(productionDepartments).map((row) => {
							return (
								<Tab
									key={productionDepartments[row].key}
									label={productionDepartments[row].name}
									value={productionDepartments[row].key}
								/>
							);
						})}
					</Tabs>
				</Box>

				<Box
					sx={{
						p: { xs: 1.25, md: 1.5 },
						borderRadius: 3,
						border: "1px solid rgba(0,0,0,0.08)",
						bgcolor: "#fff",
						boxShadow: "0 10px 28px rgba(15,23,42,0.05)",
					}}>
					{tabComponents?.map((row) => (
						<Box width="100%" margin="auto" key={row.key} display={row.key === activeDepartment ? "block" : "none"}>
							{row.component}
						</Box>
					))}
				</Box>
			</Box>
			<Fab
				size="small"
				color="primary"
				aria-label="add"
				sx={{ position: "fixed", right: "5%", bottom: "5%", boxShadow: "0 12px 24px rgba(0,0,0,0.18)" }}
				onClick={handleScroll}>
				<KeyboardArrowDown />
			</Fab>
		</Box>
	);
};

export default SelectedStyle;
